<template>
  <div class="relatorio-container">
    <!-- Header -->
    <header class="relatorio-header">
      <div class="header-content">
        <div class="header-left">
          <button @click="voltarMenu" class="back-btn">
            ← Voltar
          </button>
          <div class="title-section">
            <h1 class="page-title">Relatório de Produção</h1>
            <div class="subtitle">Abertura #{{ idAbertura }}</div>
          </div>
        </div>
        <div class="header-right">
          <button @click="debugState" class="debug-btn" style="margin-right: 10px; background: #ff6b6b;">
            🔍 Debug
          </button>
          <button @click="carregarEImprimir" class="print-btn">
            🖨️ Imprimir
          </button>
        </div>
      </div>
    </header>

    <!-- Estado Inicial -->
    <div v-show="!relatorioCarregado && !loading && !error" class="initial-state">
      <div class="initial-content">
        <div class="initial-icon">📋</div>
        <h2 class="initial-title">Relatório de Produção</h2>
        <p class="initial-description">
          Abertura #{{ idAbertura }}
        </p>
        <button @click="carregarEImprimir" class="btn-load-report">
          <span class="btn-icon">🖨️</span>
          <span class="btn-text">Carregar e Imprimir Relatório</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">⏳ Carregando relatório...</div>
    </div>

    <!-- Error -->
    <div v-show="error" class="error-container">
      <div class="error-box">
        <h2>❌ Erro ao carregar relatório</h2>
        <p>{{ error }}</p>
        <button @click="carregarEImprimir" class="retry-btn">Tentar novamente</button>
      </div>
    </div>

    <!-- Relatório Content (após carregamento) -->
    <div v-show="relatorioCarregado" class="relatorio-content" ref="relatorioContent">
      <!-- Cabeçalho do Relatório -->
      <div class="report-header">
        <div class="header-top">
          <span class="header-title">Relatório de Produção</span>
          <div class="header-meta">
            Abertura <strong>#{{ idAbertura }}</strong> &nbsp;|&nbsp;
            Emitido em: <strong>{{ dataAtual }}</strong>
          </div>
        </div>
        <div class="header-info">
          <div class="hi">📅 Abertura: <strong>{{ formatarData(dadosAbertura.data_abertura) }}</strong></div>
          <div class="hi">⏰ Encerramento: <strong>{{ formatarData(dadosAbertura.data_encerramento) }}</strong></div>
          <div class="hi">🏢 Setor: <strong>{{ dadosAbertura.setor }}</strong></div>
          <div class="hi">👤 Chefe: <strong>{{ dadosAbertura.chefe }}</strong></div>
          <div class="hi">📊 Status: <strong>{{ dadosAbertura.status }}</strong></div>
        </div>
      </div>

      <!-- KPI Strip -->
      <div class="kpi-strip">
        <div class="kpi-card">
          <span class="kpi-val">{{ funcionarios.length }}</span>
          <div class="kpi-lbl">Funcionários</div>
        </div>
        <div class="kpi-card">
          <span class="kpi-val">{{ totalOS }}</span>
          <div class="kpi-lbl">Ordens de Serviço</div>
        </div>
        <div class="kpi-card">
          <span class="kpi-val">{{ totalMinutos }}m</span>
          <div class="kpi-lbl">Tempo Total</div>
        </div>
        <div class="kpi-card">
          <span class="kpi-val" style="color:var(--green)">{{ totalUtil }}m</span>
          <div class="kpi-lbl">Tempo Útil</div>
        </div>
        <div class="kpi-card">
          <span class="kpi-val" style="color:var(--red)">{{ totalNUtil }}m</span>
          <div class="kpi-lbl">Não Útil</div>
        </div>
      </div>

      <!-- Resumo dos Funcionários -->
      <div class="section-title">👥 Resumo dos Funcionários</div>
      <div class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th class="center">#</th>
              <th>Nome</th>
              <th>Cargo</th>
              <th>Setor</th>
              <th class="center">Entrada</th>
              <th class="center">Saída</th>
              <th class="center">Total</th>
              <th class="center">Útil</th>
              <th class="center">Não Útil</th>
              <th class="center">OS</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(func, index) in funcionarios" :key="func.id_funcionario">
              <td class="center">{{ index + 1 }}</td>
              <td class="td-nome">{{ func.nome }}</td>
              <td class="desc-cell">{{ func.cargo }}</td>
              <td class="desc-cell">{{ func.setor }}</td>
              <td class="center">{{ formatarHora(func.data_entrada) }}</td>
              <td class="center">{{ formatarHora(func.data_saida) }}</td>
              <td class="center td-tot">{{ func.tempo_total }}m</td>
              <td class="center td-util">{{ func.tempo_util }}m</td>
              <td class="center td-nutil">{{ func.tempo_nao_util }}m</td>
              <td class="center td-os">{{ func.qtd_os }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="3" class="right"><strong>Total</strong></td>
              <td class="center"><strong>{{ totalMinutos }}m</strong></td>
              <td class="center"><strong>{{ totalUtil }}m</strong></td>
              <td class="center"><strong>{{ totalNUtil }}m</strong></td>
              <td class="center"><strong>{{ totalOS }}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Detalhes por Funcionário -->
      <div v-for="func in funcionarios" :key="func.id_funcionario" class="func-section">
        <div class="func-header">
          <div class="func-label">Funcionário</div>
          <div class="func-id">ID: {{ func.id_funcionario }}</div>
        </div>
        <div class="func-name">{{ func.nome }}</div>
        <div class="func-stats">
          <div class="stat-item">
            <div class="stat-label">Entrada</div>
            <div class="stat-value">{{ formatarHora(func.data_entrada) }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Saída</div>
            <div class="stat-value">{{ formatarHora(func.data_saida) }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Total</div>
            <div class="stat-value">{{ func.tempo_total }}m</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Útil</div>
            <div class="stat-value green">{{ func.tempo_util }}m</div>
          </div>
        </div>
        <div class="func-divider"></div>
        
        <div class="func-bar">
          <div class="bar-title">📋 Ordens de Serviço</div>
        </div>
        
        <div class="func-table">
          <table class="data-table">
            <thead>
              <tr>
                <th class="center">#</th>
                <th>Tipo</th>
                <th>Descrição</th>
                <th class="center">Início</th>
                <th class="center">Fim</th>
                <th class="center">Tempo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="os in func.ordens" :key="os.id">
                <td class="center">{{ os.id }}</td>
                <td class="center">
                  <span class="badge" :class="os.tipo === 'PRODUÇÃO' ? 'badge-green' : ''">
                    {{ os.tipo }}
                  </span>
                </td>
                <td>{{ os.descricao }}</td>
                <td class="center">{{ formatarHora(os.data_inicio) }}</td>
                <td class="center">{{ formatarHora(os.data_fim) }}</td>
                <td class="center">{{ os.tempo }}m</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="5" class="right"><strong>Total</strong></td>
                <td class="center"><strong>{{ func.tempo_total }}m</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!-- Menu Lateral -->
  <aside class="sidebar" :class="{ 'sidebar-open': sidebarOpen }">
    <div class="sidebar-header">
      <div class="logo">
        <span class="logo-icon">🏢</span>
        <span class="logo-text">Tabosa Sistemas</span>
      </div>
      <button @click="toggleSidebar" class="sidebar-toggle">
        <span class="hamburger"></span>
      </button>
    </div>
    
    <div class="sidebar-content">
      <div class="sidebar-user">
        <div class="sidebar-avatar">👤</div>
        <div class="sidebar-user-info">
          <div class="sidebar-user-name">{{ userName }}</div>
          <div class="sidebar-user-role">Administrador</div>
        </div>
      </div>
      
      <nav class="sidebar-nav">
        <a @click="navegarPara('/dashboard')" class="nav-item" :class="{ active: $route.path === '/dashboard' || $route.path.startsWith('/dashboard') }">
          <span class="nav-icon">📊</span>
          <span class="nav-text">Dashboard</span>
        </a>
        <a @click="navegarPara('/dashboard/relatorios')" class="nav-item" :class="{ active: $route.path === '/dashboard/relatorios' }">
          <span class="nav-icon">📈</span>
          <span class="nav-text">Relatórios</span>
        </a>
        <a @click="logout" class="nav-item">
          <span class="nav-icon">🚪</span>
          <span class="nav-text">Sair</span>
        </a>
      </nav>
    </div>
  </aside>

  <!-- Overlay -->
  <div class="sidebar-overlay" :class="{ 'sidebar-overlay-open': sidebarOpen }" @click="closeSidebar"></div>
  
  <!-- Loading de Impressão -->
  <div v-if="loadingImpressao" class="loading-overlay">
    <div class="loading-card">
      <div class="loading-icon">🖨️</div>
      <h3>Gerando Relatório</h3>
      <p>Aguarde enquanto preparamos seu relatório para impressão...</p>
      <div class="loading-bar">
        <div class="loading-progress"></div>
      </div>
      <small>Isso pode levar alguns segundos</small>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RelatorioDetalhadoPage',
  data() {
    return {
      loading: false,
      loadingImpressao: false,
      error: null,
      idAbertura: null,
      dadosAbertura: {},
      funcionarios: [],
      totalOS: 0,
      totalMinutos: 0,
      totalUtil: 0,
      totalNUtil: 0,
      relatorioCarregado: false,
      sidebarOpen: false,
      userName: 'Usuário'
    }
  },
  computed: {
    dataAtual() {
      return new Date().toLocaleString('pt-BR')
    }
  },
  mounted() {
    console.log('Componente RelatorioDetalhado montado!')
    this.idAbertura = this.$route.query.id_abertura
    console.log('ID da abertura:', this.idAbertura)
    
    if (!this.idAbertura) {
      console.log('ID da abertura não informado')
      this.error = 'ID da abertura não informado'
    }
  },
  methods: {
    async carregarRelatorio() {
      console.log('Iniciando carregarRelatorio')
      this.loading = true
      this.error = null
      
      try {
        console.log('Carregando relatório para ID:', this.idAbertura)
        // Temporariamente usando API de teste para debug
        const response = await fetch(`/api/relatorio_detalhado_teste.php?id_abertura=${this.idAbertura}`)
        console.log('Status da resposta:', response.status)
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const data = await response.json()
        console.log('Dados recebidos:', data)
        
        if (data.success) {
          this.dadosAbertura = data.dados_abertura || {}
          this.funcionarios = data.funcionarios || []
          this.totalOS = data.total_os || 0
          this.totalMinutos = data.total_minutos || 0
          this.totalUtil = data.total_util || 0
          this.totalNUtil = data.total_nao_util || 0
          this.relatorioCarregado = true
          console.log('Dados carregados com sucesso!')
          console.log('relatorioCarregado:', this.relatorioCarregado)
        } else {
          console.error('Erro na API:', data.error)
          this.error = data.error || 'Erro ao carregar relatório'
          this.relatorioCarregado = false
        }
      } catch (error) {
        console.error('Erro ao carregar relatório:', error)
        this.error = 'Erro de conexão. Tente novamente.'
        this.relatorioCarregado = false
      } finally {
        console.log('Finalizando carregamento')
        this.loading = false
        console.log('Loading finalizado:', this.loading)
        console.log('relatorioCarregado final:', this.relatorioCarregado)
        
        // Forçar atualização reativa do Vue
        this.$forceUpdate()
        
        // Forçar parada do loading após 1 segundo (backup)
        setTimeout(() => {
          console.log('Forçando parada do loading (backup)')
          this.loading = false
          this.$forceUpdate()
        }, 1000)
      }
    },
    
    voltarMenu() {
      this.$router.push('/menu/dashboard')
    },
    
    debugState() {
      console.log('=== ESTADO ATUAL ===')
      console.log('loading:', this.loading)
      console.log('error:', this.error)
      console.log('relatorioCarregado:', this.relatorioCarregado)
      console.log('idAbertura:', this.idAbertura)
      console.log('dadosAbertura:', this.dadosAbertura)
      console.log('funcionarios:', this.funcionarios?.length || 0)
      console.log('==================')
    },
    
    async carregarEImprimir() {
      // Mostrar loading de impressão
      this.loadingImpressao = true
      
      await this.carregarRelatorio()
      
      // Forçar parada do loading após 3 segundos (emergência)
      setTimeout(() => {
        if (this.loading) {
          console.log('Forçando parada do loading (emergência)')
          this.loading = false
        }
      }, 3000)
      
      if (this.relatorioCarregado) {
        // Pequeno delay para garantir que o conteúdo foi renderizado
        setTimeout(() => {
          this.imprimirRelatorio()
          
          // Fechar loading após impressão
          setTimeout(() => {
            this.loadingImpressao = false
          }, 2000)
        }, 500)
      } else {
        // Fechar loading se não carregou
        this.loadingImpressao = false
      }
    },
    
    voltar() {
      this.$router.go(-1)
    },

    imprimir() {
      window.print()
    },

    onIframeLoad() {
      this.loading = false
    },

    formatarData(data) {
      if (!data) return 'N/A'
      return new Date(data).toLocaleString('pt-BR')
    },

    formatarTempo(minutos) {
      if (!minutos) return '0m'
      const horas = Math.floor(minutos / 60)
      const mins = minutos % 60
      return horas > 0 ? `${horas}h ${mins}m` : `${mins}m`
    },

    imprimirRelatorio() {
      const conteudo = this.$refs.relatorioContent.innerHTML
      const janelaImpressao = window.open('', '_blank')
      
      janelaImpressao.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Relatório de Produção - Abertura #${this.idAbertura}</title>
          <style>
            ${this.getEstilosImpressao()}
          </style>
        </head>
        <body>
          ${conteudo}
        </body>
        </html>
      `)
      
      janelaImpressao.document.close()
      janelaImpressao.focus()
      
      setTimeout(() => {
        janelaImpressao.print()
        janelaImpressao.close()
      }, 500)
    },
    
    getEstilosImpressao() {
      return `
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; font-size: 10px; color: #1a2e4a; }
        .report-header { margin-bottom: 20px; }
        .header-title { font-size: 14px; font-weight: bold; color: #4a6fa5; }
        .header-meta { font-size: 9px; color: #64748b; margin-top: 4px; }
        .header-info { display: flex; flex-wrap: wrap; gap: 15px; margin-top: 8px; }
        .hi { font-size: 8.5px; color: #475569; }
        .hi strong { color: #2d4e82; }
        .kpi-strip { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-bottom: 20px; }
        .kpi-card { border: 1px solid #d1daea; border-top: 2px solid #4a6fa5; border-radius: 5px; padding: 10px; text-align: center; }
        .kpi-val { display: block; font-size: 14px; font-weight: bold; color: #4a6fa5; }
        .kpi-lbl { font-size: 7.5px; text-transform: uppercase; color: #64748b; margin-top: 2px; }
        .section-title { font-size: 9px; font-weight: bold; color: #4a6fa5; border-left: 3px solid #4a6fa5; padding-left: 6px; margin: 20px 0 10px; }
        .data-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        .data-table th { background: #e8f0fe; color: #2d4e82; font-weight: bold; font-size: 8px; padding: 5px; border: 1px solid #d1daea; }
        .data-table td { padding: 4px 6px; border: 1px solid #edf0f7; font-size: 9px; }
        .data-table tfoot td { background: #dde7f7; font-weight: bold; }
        .center { text-align: center; }
        .right { text-align: right; }
        .td-nome { font-weight: bold; font-size: 11px; }
        .td-util { color: #16a34a; font-weight: bold; }
        .td-nutil { color: #dc2626; font-weight: bold; }
        .func-section { page-break-before: always; margin-top: 30px; }
        .func-header { margin-bottom: 5px; }
        .func-label { font-size: 8.5px; color: #64748b; text-transform: uppercase; }
        .func-name { font-size: 14px; font-weight: bold; color: #2d4e82; margin: 2px 0; }
        .func-stats { display: flex; gap: 20px; margin-bottom: 10px; }
        .stat-item { text-align: center; }
        .stat-label { font-size: 7px; color: #64748b; text-transform: uppercase; }
        .stat-value { font-size: 11px; font-weight: bold; }
        .stat-value.green { color: #16a34a; }
        .func-divider { border-top: 1.5px solid #c7d9f5; margin: 10px 0; }
        .func-bar { background: #e8f0fe; border-left: 4px solid #4a6fa5; padding: 6px 10px; margin-bottom: 0; }
        .bar-title { font-size: 9px; font-weight: bold; color: #2d4e82; text-transform: uppercase; }
        .badge { background: #e8f0fe; color: #2d4e82; padding: 1px 4px; border-radius: 3px; font-size: 8px; font-weight: bold; }
        .badge-green { background: #dcfce7; color: #16a34a; }
        @media print { .no-print { display: none; } }
      `
    },

    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen
    },

    closeSidebar() {
      this.sidebarOpen = false
    },

    navegarPara(path) {
      this.$router.push(path)
      this.closeSidebar()
    },

    logout() {
      // Implement logout functionality
      this.$router.push('/login')
    }
  }
}
</script>


<style scoped>
/* ── Variáveis ─────────────────────────────────────── */
:root {
  --accent: #2563eb;
  --accent-soft: #dbeafe;
  --accent-strong: #1d4ed8;
  --heading: #1e293b;
  --text: #475569;
  --muted: #94a3b8;
  --card-bg: #ffffff;
  --filter-bg: #f8fafc;
  --border: #e2e8f0;
}

*, *::before, *::after { box-sizing: border-box; }

body {
  margin: 0;
  min-height: 100vh;
  font-family: 'Poppins', 'Segoe UI', system-ui, sans-serif;
  background: linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 100%);
  color: var(--text);
}

[v-cloak] { display: none; }

/* ── Menu Lateral ─────────────────────────────────────── */
.sidebar {
  position: fixed;
  top: 0;
  left: -280px;
  width: 280px;
  height: 100vh;
  background: var(--card-bg);
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  transition: left 0.3s ease;
  z-index: 1000;
}

.sidebar-open {
  left: 0;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid var(--border);
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  font-size: 24px;
}

.logo-text {
  font-size: 18px;
  font-weight: 700;
  color: var(--heading);
}

.sidebar-toggle {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background 0.2s;
}

.sidebar-toggle:hover {
  background: var(--filter-bg);
}

.hamburger {
  display: block;
  width: 20px;
  height: 2px;
  background: var(--text);
  position: relative;
}

.hamburger::before,
.hamburger::after {
  content: '';
  position: absolute;
  width: 20px;
  height: 2px;
  background: var(--text);
}

.hamburger::before {
  top: -6px;
}

.hamburger::after {
  top: 6px;
}

.sidebar-content {
  padding: 20px;
}

.sidebar-user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 0;
  border-bottom: 1px solid var(--border);
  margin-bottom: 20px;
}

.sidebar-avatar {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #1976d2, #2196f3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.sidebar-user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.sidebar-user-name {
  font-weight: 600;
  color: var(--heading);
  font-size: 1rem;
  line-height: 1.2;
}

.sidebar-user-role {
  font-size: 0.875rem;
  color: var(--muted);
  line-height: 1.2;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: var(--text);
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s;
  margin-bottom: 4px;
}

.nav-item:hover {
  background: var(--filter-bg);
  color: var(--accent);
}

.nav-item.active {
  background: var(--accent-soft);
  color: var(--accent);
}

.nav-icon {
  font-size: 20px;
}

.nav-text {
  font-weight: 500;
}

/* ── Overlay ─────────────────────────────────────── */
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 999;
}

.sidebar-overlay-open {
  opacity: 1;
  visibility: visible;
}

/* ── Conteúdo Principal ─────────────────────────────────────── */
.main-content {
  margin-left: 0;
  transition: margin-left 0.3s ease;
  min-height: 100vh;
}

.main-content-shifted {
  margin-left: 280px;
}

.content-wrapper {
  padding: 20px;
}

/* ── Header ─────────────────────────────────────── */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding: 20px;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.mobile-menu-btn {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
}

.mobile-menu-btn:hover {
  background: var(--filter-bg);
}

.page-title h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--heading);
}

.page-subtitle {
  margin: 4px 0 0;
  font-size: 14px;
  color: var(--muted);
}

.header-right {
  display: flex;
  gap: 12px;
}

.btn-back,
.btn-print {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-back {
  background: var(--filter-bg);
  color: var(--text);
}

.btn-back:hover {
  background: var(--border);
}

.btn-print {
  background: var(--accent);
  color: white;
}

.btn-print:hover {
  background: var(--accent-strong);
}

/* ── Container do Relatório ─────────────────────────────────────── */
.report-container {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  min-height: 600px;
}

.report-iframe {
  width: 100%;
  height: 80vh;
  border: none;
}

/* ── Loading ─────────────────────────────────────── */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: var(--muted);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border);
  border-top: 3px solid var(--accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ── Responsivo ─────────────────────────────────────── */
@media (max-width: 768px) {
  .mobile-menu-btn {
    display: block;
  }
  
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .header-right {
    width: 100%;
    justify-content: flex-end;
  }
  
  .content-wrapper {
    padding: 10px;
  }
  
  .report-iframe {
    height: 60vh;
  }
}

/* ── Vuetify overrides ─────────────────────────────────────── */
.v-application { background: transparent !important; }
</style>

<style scoped>
.relatorio-container {
  min-height: 100vh;
  background: #f0f4ff;
}

.relatorio-header {
  background: linear-gradient(135deg, #1565c0 0%, #1976d2 50%, #2196f3 100%);
  box-shadow: 0 4px 20px rgba(25, 118, 210, 0.3);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  color: white;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.page-title {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
}

.subtitle {
  font-size: 0.875rem;
  opacity: 0.8;
  margin-top: 2px;
}

.print-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.print-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.relatorio-content {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.loading-container, .error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-spinner, .error-box {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.error-box h2 {
  color: #dc2626;
  margin-bottom: 16px;
}

.error-box p {
  color: #64748b;
  margin-bottom: 20px;
}

.retry-btn {
  background: #1976d2;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
}

/* Estado Inicial */
.initial-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: 40px 20px;
}

.initial-content {
  text-align: center;
  background: white;
  border-radius: 16px;
  padding: 60px 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  width: 100%;
}

.initial-icon {
  font-size: 4rem;
  margin-bottom: 20px;
}

.initial-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.initial-description {
  font-size: 1rem;
  color: #64748b;
  margin-bottom: 30px;
}

.btn-load-report {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.3);
}

.btn-load-report:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
}

.btn-icon {
  font-size: 1.2rem;
}

.btn-text {
  font-weight: 600;
}

/* Estilos do relatório (mesmos do PHP) */
:root {
  --pri: #4a6fa5;
  --pri-soft: #e8f0fe;
  --pri-mid: #c7d9f5;
  --pri-dark: #2d4e82;
  --green: #16a34a;
  --red: #dc2626;
  --text: #1a2e4a;
  --muted: #64748b;
  --border: #d1daea;
}

.report-header {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}

.header-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--pri);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.header-meta {
  font-size: 8.5px;
  color: var(--muted);
  text-align: right;
}

.header-info {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  font-size: 8.5px;
  color: var(--text);
}

.hi {
  display: flex;
  align-items: center;
  gap: 4px;
}

.hi strong {
  color: var(--pri-dark);
  font-weight: 700;
}

.kpi-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.kpi-card {
  background: white;
  border: 1px solid var(--border);
  border-top: 2px solid var(--pri);
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.kpi-val {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: var(--pri);
  line-height: 1;
}

.kpi-lbl {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--muted);
  margin-top: 4px;
}

.section-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--pri);
  border-left: 3px solid var(--pri);
  padding-left: 12px;
  margin: 24px 0 16px;
}

.table-wrap {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.data-table th {
  background: var(--pri-soft);
  color: var(--pri-dark);
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 12px;
  border-bottom: 1px solid var(--pri-mid);
}

.data-table td {
  padding: 12px;
  border-bottom: 1px solid #f3f4f6;
  color: var(--text);
}

.data-table tfoot td {
  background: #f8fafc;
  font-weight: 700;
}

.center {
  text-align: center;
}

.right {
  text-align: right;
}

.td-nome {
  font-size: 1rem;
  font-weight: 700;
  color: var(--pri-dark);
}

.td-util {
  color: var(--green);
  font-weight: 700;
}

.td-nutil {
  color: var(--red);
  font-weight: 600;
}

.td-tot {
  font-weight: 700;
}

.func-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.func-header {
  margin-bottom: 8px;
}

.func-label {
  font-size: 0.75rem;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.func-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--pri-dark);
  margin: 4px 0;
}

.func-id {
  font-size: 0.75rem;
  color: var(--muted);
}

.func-stats {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
}

.stat-item {
  text-align: center;
}

.stat-label {
  font-size: 0.625rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--muted);
}

.stat-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
}

.stat-value.green {
  color: var(--green);
}

.func-divider {
  border: none;
  border-top: 1.5px solid var(--pri-mid);
  margin: 16px 0;
}

.func-bar {
  display: flex;
  align-items: center;
  background: var(--pri-soft);
  border-left: 4px solid var(--pri);
  padding: 8px 16px;
  margin-bottom: 0;
}

.bar-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--pri-dark);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.badge {
  display: inline-block;
  background: var(--pri-soft);
  color: var(--pri-dark);
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 0.75rem;
  font-weight: 700;
}

/* Loading de Impressão */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-card {
  background: white;
  padding: 32px;
  border-radius: 16px;
  text-align: center;
  max-width: 320px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.loading-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.loading-card h3 {
  margin: 0 0 12px 0;
  color: #1e293b;
  font-size: 1.25rem;
}

.loading-card p {
  margin: 0 0 20px 0;
  color: #64748b;
  font-size: 0.9rem;
}

.loading-bar {
  width: 100%;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 12px;
}

.loading-progress {
  height: 100%;
  background: #2563eb;
  border-radius: 2px;
  animation: loading-progress 1.5s ease-in-out infinite;
}

@keyframes loading-progress {
  0% { width: 0%; }
  50% { width: 70%; }
  100% { width: 100%; }
}

.loading-card small {
  color: #94a3b8;
  font-size: 0.8rem;
}

.badge-green {
  background: #dcfce7;
  color: var(--green);
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  
  .kpi-strip {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .func-stats {
    flex-wrap: wrap;
    gap: 16px;
  }
  
  .data-table {
    font-size: 0.75rem;
  }
  
  .data-table th,
  .data-table td {
    padding: 8px 4px;
  }
}
</style>
